import React, { useState } from "react";
import { X, Upload, Music, Trash2, Play, Pause } from "lucide-react";

const UploadAudioPopup = ({ order, onClose, onUpload }) => {
    const [audioFiles, setAudioFiles] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [playingAudio, setPlayingAudio] = useState(null);

    const handleFileSelect = (event) => {
        const files = Array.from(event.target.files);
        const newAudioFiles = files.map(file => ({
            file,
            id: Math.random().toString(36).substr(2, 9),
            name: file.name,
            size: file.size,
            type: file.type,
            duration: 0, // You can extract this using Web Audio API
            uploadedAt: new Date()
        }));
        setAudioFiles(prev => [...prev, ...newAudioFiles]);
        event.target.value = ""; // Reset input
    };

    const removeFile = (fileId) => {
        setAudioFiles(prev => prev.filter(file => file.id !== fileId));
        if (playingAudio === fileId) {
            setPlayingAudio(null);
        }
    };

    const togglePlay = (fileId) => {
        if (playingAudio === fileId) {
            setPlayingAudio(null);
        } else {
            setPlayingAudio(fileId);
        }
    };

    const handleUpload = async () => {
        if (audioFiles.length === 0) return;

        setUploading(true);
        try {
            // Simulate upload process
            await new Promise(resolve => setTimeout(resolve, 2000));

            // Here you would typically upload to your backend
            const uploadedFiles = audioFiles.map(audioFile => ({
                id: audioFile.id,
                name: audioFile.name,
                url: URL.createObjectURL(audioFile.file), // In real app, this would be server URL
                duration: audioFile.duration,
                uploadedAt: new Date().toISOString(),
                orderId: order.id
            }));

            onUpload(uploadedFiles);
            onClose();
        } catch (error) {
            console.error("Upload failed:", error);
        } finally {
            setUploading(false);
        }
    };

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const formatDuration = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl mx-4">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-purple-100 rounded-lg">
                            <Music className="w-5 h-5 text-purple-600" />
                        </div>
                        <div>
                            <h3 className="text-lg font-semibold text-gray-900">
                                Upload Audio Files
                            </h3>
                            <p className="text-sm text-gray-600">
                                For {order.companyName || order.company_name}
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Body */}
                <div className="p-6 space-y-4">
                    {/* Upload Area */}
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-purple-400 transition-colors">
                        <Upload className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                        <p className="text-sm text-gray-600 mb-2">
                            Drop your audio files here or click to browse
                        </p>
                        <p className="text-xs text-gray-500 mb-4">
                            Supports .mp3, .wav, .m4a, .aac (Max 50MB each)
                        </p>
                        <input
                            type="file"
                            multiple
                            accept=".mp3,.wav,.m4a,.aac"
                            onChange={handleFileSelect}
                            className="hidden"
                            id="audio-upload"
                        />
                        <label
                            htmlFor="audio-upload"
                            className="inline-flex items-center gap-2 bg-purple-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-purple-700 cursor-pointer transition-colors"
                        >
                            <Upload className="w-4 h-4" />
                            Choose Files
                        </label>
                    </div>

                    {/* File List */}
                    {audioFiles.length > 0 && (
                        <div className="space-y-3">
                            <h4 className="text-sm font-medium text-gray-900">
                                Selected Files ({audioFiles.length})
                            </h4>
                            <div className="space-y-2 max-h-60 overflow-y-auto">
                                {audioFiles.map((audioFile) => (
                                    <div
                                        key={audioFile.id}
                                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border"
                                    >
                                        <div className="flex items-center gap-3 flex-1">
                                            <button
                                                onClick={() => togglePlay(audioFile.id)}
                                                className="p-2 bg-purple-100 text-purple-600 rounded-full hover:bg-purple-200 transition-colors"
                                            >
                                                {playingAudio === audioFile.id ? (
                                                    <Pause className="w-3 h-3" />
                                                ) : (
                                                    <Play className="w-3 h-3" />
                                                )}
                                            </button>
                                            <Music className="w-4 h-4 text-gray-600" />
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm font-medium text-gray-900 truncate">
                                                    {audioFile.name}
                                                </p>
                                                <div className="flex items-center gap-4 text-xs text-gray-500">
                                                    <span>{formatFileSize(audioFile.size)}</span>
                                                    {audioFile.duration > 0 && (
                                                        <span>{formatDuration(audioFile.duration)}</span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                        <button
                                            onClick={() => removeFile(audioFile.id)}
                                            className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="flex justify-end gap-3 px-6 py-4 border-t bg-gray-50 rounded-b-xl">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 text-sm text-gray-700 hover:text-gray-900 font-medium transition-colors"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleUpload}
                        disabled={audioFiles.length === 0 || uploading}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                        {uploading ? (
                            <>
                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                Uploading...
                            </>
                        ) : (
                            <>
                                <Upload className="w-4 h-4" />
                                Upload {audioFiles.length} File{audioFiles.length !== 1 ? 's' : ''}
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default UploadAudioPopup;